/**
 * 提供商配置页（T15）可见部件元数据。
 *
 * 本文件是「安卓移植清单」的数据源之一：页面里每个可以看到的部件都在这里声明
 * id / 角色 / 中文名 / 390x844 逻辑坐标 / zIndex / props / notes；页面组件通过
 * `useComponentMeta` 把同一份元数据登记进 `src/ui/registry.tsx`。
 *
 * 坐标约定：与 App 外壳一致，一律是 390x844 画布上的绝对逻辑坐标
 * （x 向右、y 向下，左上角为原点），页面内容区从 y=24 的状态栏安全区之下开始。
 */
import type { ComponentMeta } from '../../core/types';
import type { ProviderKind } from '../../core/providers';
import type { BoxLayout, ComponentMetaInput } from '../../ui/meta';
import { BUILTIN_PROVIDER_KINDS } from '../../core/providers';

/** 本页在组件清单里的 page 标识。 */
export const PAGE_ID = 'providers';

/** 页面内容区顶部的状态栏安全区偏移（画布 y）。 */
export const PAGE_TOP = 24;

interface MetaOptions {
  zIndex?: number;
  props?: string[];
  notes?: string;
}

/**
 * role → 安卓控件建议（T18 notes 补全）。
 *
 * 未显式写 `notes` 的部件用这张表兜底，保证清单里「每个可见部件都有一句安卓用什么控件 +
 * 关键属性」；显式写了 `notes` 的部件保持自己的更精确提示。
 * 关键属性统一是「按 390x844 锚点换算 dp 定位」与触摸目标下限。
 */
const ANDROID_CONTROL_BY_ROLE: Record<NonNullable<ComponentMetaInput['role']>, string> = {
  background: 'View 背景层（配 --color-overlay）',
  container: 'FrameLayout 或 ConstraintLayout 容器',
  button: 'MaterialButton（minHeight=44dp、圆角 radius-full）',
  input: 'TextInputEditText + TextInputLayout',
  text: 'TextView（按 390x844 锚点换算 dp 定位，单行时 ellipsize=end）',
  icon: 'ImageView 或 ShapeableImageView',
  list: 'RecyclerView（item 布局复用同一份锚点）',
  modal: 'MaterialAlertDialog（dim 遮罩 + 二次确认）',
  nav: 'BottomNavigationView',
};

/** 兜底移植提示：控件 + 关键属性（安卓最小触摸目标 44dp）。 */
function defaultNotes(label: string, role: NonNullable<ComponentMetaInput['role']>): string {
  return `${label}；安卓用 ${ANDROID_CONTROL_BY_ROLE[role]}；锚点即 390x844 画布下的 dp 定位，可点部件保持 ≥44dp 触摸目标`;
}

function meta(
  id: string,
  label: string,
  role: ComponentMetaInput['role'],
  layout: BoxLayout,
  options: MetaOptions = {},
): ComponentMetaInput {
  return {
    id,
    page: PAGE_ID,
    label,
    role,
    layout,
    zIndex: options.zIndex ?? 0,
    ...(options.props === undefined ? {} : { props: options.props }),
    notes: options.notes ?? defaultNotes(label, role ?? 'container'),
  };
}

/* -------------------------------------------------------------------------- *
 * 列表页布局
 * -------------------------------------------------------------------------- */

const ROOT: BoxLayout = { x: 0, y: 24, width: 390, height: 744 };
const HEADER: BoxLayout = { x: 0, y: 24, width: 390, height: 56 };
const HEADER_TITLE: BoxLayout = { x: 16, y: 36, width: 250, height: 32 };
const HEADER_ADD: BoxLayout = { x: 330, y: 32, width: 44, height: 44 };
export const PROVIDER_LIST_LAYOUT: BoxLayout = { x: 0, y: 80, width: 390, height: 688 };
const EMPTY: BoxLayout = { x: 0, y: 320, width: 390, height: 120 };
const EMPTY_TITLE: BoxLayout = { x: 0, y: 336, width: 390, height: 32 };
const EMPTY_HINT: BoxLayout = { x: 0, y: 372, width: 390, height: 24 };

/** 列表项行高（dp）。 */
export const PROVIDER_ITEM_HEIGHT = 112;

/** 第 index 个提供商卡片的画布布局。 */
export function providerItemLayout(index: number): BoxLayout {
  return {
    x: 0,
    y: PROVIDER_LIST_LAYOUT.y + index * PROVIDER_ITEM_HEIGHT,
    width: 390,
    height: PROVIDER_ITEM_HEIGHT,
  };
}

const ITEM_PART_OFFSETS = {
  name: { x: 16, y: 10, width: 200, height: 22 },
  kind: { x: 16, y: 34, width: 200, height: 18 },
  baseUrl: { x: 16, y: 54, width: 352, height: 16 },
  defaultModel: { x: 16, y: 72, width: 352, height: 16 },
  enabledToggle: { x: 222, y: 10, width: 48, height: 44 },
  editButton: { x: 276, y: 10, width: 48, height: 44 },
  deleteButton: { x: 330, y: 10, width: 48, height: 44 },
} as const;

export type ProviderItemPart = keyof typeof ITEM_PART_OFFSETS;

/** 列表项内部部件的画布布局。 */
export function providerItemPartLayout(item: BoxLayout, part: ProviderItemPart): BoxLayout {
  const offset = ITEM_PART_OFFSETS[part];
  return {
    x: item.x + offset.x,
    y: item.y + offset.y,
    width: offset.width,
    height: offset.height,
  };
}

/** 删除确认弹层布局。 */
const DELETE_BACKDROP: BoxLayout = { x: 0, y: 24, width: 390, height: 744 };
const DELETE_DIALOG: BoxLayout = { x: 45, y: 330, width: 300, height: 180 };
const DELETE_TITLE: BoxLayout = { x: 65, y: 348, width: 260, height: 28 };
const DELETE_TEXT: BoxLayout = { x: 65, y: 380, width: 260, height: 40 };
const DELETE_CANCEL: BoxLayout = { x: 60, y: 434, width: 130, height: 44 };
const DELETE_CONFIRM: BoxLayout = { x: 200, y: 434, width: 130, height: 44 };

/* -------------------------------------------------------------------------- *
 * 配置表单布局
 * -------------------------------------------------------------------------- */

const FORM_HEADER: BoxLayout = HEADER;
const FORM_BACK: BoxLayout = { x: 12, y: 32, width: 44, height: 44 };
const FORM_TITLE: BoxLayout = { x: 64, y: 38, width: 310, height: 32 };
const KIND_LABEL: BoxLayout = { x: 16, y: 92, width: 358, height: 18 };
const KIND_ROW_Y = 112;
const KIND_ITEM_WIDTH = 85;
const KIND_HINT: BoxLayout = { x: 16, y: 160, width: 358, height: 16 };
const PURPOSE_LABEL: BoxLayout = { x: 16, y: 184, width: 358, height: 18 };
const PURPOSE_SELECT: BoxLayout = { x: 16, y: 204, width: 358, height: 44 };
const PURPOSE_ERROR: BoxLayout = { x: 16, y: 250, width: 358, height: 14 };
const NAME_LABEL: BoxLayout = { x: 16, y: 270, width: 358, height: 18 };
const NAME_INPUT: BoxLayout = { x: 16, y: 290, width: 358, height: 44 };
const NAME_ERROR: BoxLayout = { x: 16, y: 336, width: 358, height: 14 };
const BASE_URL_LABEL: BoxLayout = { x: 16, y: 356, width: 358, height: 18 };
const BASE_URL_INPUT: BoxLayout = { x: 16, y: 376, width: 358, height: 44 };
const BASE_URL_ERROR: BoxLayout = { x: 16, y: 422, width: 358, height: 14 };
const API_KEY_LABEL: BoxLayout = { x: 16, y: 442, width: 358, height: 18 };
const API_KEY_INPUT: BoxLayout = { x: 16, y: 462, width: 302, height: 44 };
const API_KEY_REVEAL: BoxLayout = { x: 324, y: 462, width: 50, height: 44 };
const DEFAULT_MODEL_LABEL: BoxLayout = { x: 16, y: 514, width: 358, height: 18 };
const DEFAULT_MODEL_INPUT: BoxLayout = { x: 16, y: 534, width: 358, height: 44 };
const ENABLED_LABEL: BoxLayout = { x: 16, y: 584, width: 240, height: 44 };
const ENABLED_TOGGLE: BoxLayout = { x: 296, y: 584, width: 78, height: 44 };
const TEST_BUTTON: BoxLayout = { x: 16, y: 632, width: 180, height: 44 };
const TEST_RESULT: BoxLayout = { x: 16, y: 676, width: 358, height: 40 };
const SAVE_BUTTON: BoxLayout = { x: 16, y: 720, width: 180, height: 44 };
const CANCEL_BUTTON: BoxLayout = { x: 200, y: 720, width: 174, height: 44 };

/** kind 分段控件的画布布局。 */
export const PROVIDER_KIND_ITEM_LAYOUTS: Record<ProviderKind, BoxLayout> = (() => {
  const layouts = {} as Record<ProviderKind, BoxLayout>;
  BUILTIN_PROVIDER_KINDS.forEach((kind, index) => {
    layouts[kind] = {
      x: 16 + index * 91,
      y: KIND_ROW_Y,
      width: KIND_ITEM_WIDTH,
      height: 44,
    };
  });
  return layouts;
})();

/* -------------------------------------------------------------------------- *
 * 导出：静态部件元数据（列表模板 + 表单 + 删除弹层）
 * -------------------------------------------------------------------------- */

const ITEM_TEMPLATE = providerItemLayout(0);

const LIST_COMPONENTS: ComponentMetaInput[] = [
  meta('page.providers.root', '提供商配置页容器', 'container', ROOT, {
    zIndex: 2,
    notes: '列表视图根容器（坐标含 24dp 状态栏偏移）；安卓用 Fragment 根 View',
  }),
  meta('providers.header', '提供商页头', 'container', HEADER, {
    zIndex: 3,
    notes: '标题 + 新增按钮容器；安卓用 MaterialToolbar',
  }),
  meta('providers.header.title', '页面标题：模型提供商', 'text', HEADER_TITLE, {
    zIndex: 4,
    notes: '安卓用 Toolbar 标题',
  }),
  meta('providers.header.addButton', '新增提供商', 'button', HEADER_ADD, {
    zIndex: 4,
    props: ['pressed'],
    notes: '安卓用 FloatingActionButton（mini）',
  }),
  meta('providers.list', '提供商列表', 'list', PROVIDER_LIST_LAYOUT, {
    zIndex: 3,
    props: ['items'],
    notes: '安卓用 RecyclerView；卡片行高 112dp，第 n 行 y = 80 + 112n',
  }),
  meta('providers.item', '提供商卡片（列表项模板）', 'container', ITEM_TEMPLATE, {
    zIndex: 4,
    props: ['kind', 'purpose', 'name', 'baseUrl', 'defaultModel', 'enabled'],
    notes: '列表项模板；运行时按提供商 id 登记为 providers.item.<id>',
  }),
  meta('providers.item.name', '提供商名称', 'text', providerItemPartLayout(ITEM_TEMPLATE, 'name'), {
    zIndex: 5,
    notes: '单行省略号',
  }),
  meta('providers.item.kind', '提供商类型与密钥状态', 'text', providerItemPartLayout(ITEM_TEMPLATE, 'kind'), {
    zIndex: 5,
    notes: '格式：类型 · 已配置密钥 / 未配置密钥',
  }),
  meta('providers.item.baseUrl', '提供商基址', 'text', providerItemPartLayout(ITEM_TEMPLATE, 'baseUrl'), {
    zIndex: 5,
    notes: '单行省略号',
  }),
  meta('providers.item.defaultModel', '默认模型', 'text', providerItemPartLayout(ITEM_TEMPLATE, 'defaultModel'), {
    zIndex: 5,
    notes: '格式：默认模型：xxx（未设置时提示未选择）',
  }),
  meta('providers.item.enabledToggle', '启用 / 停用', 'button', providerItemPartLayout(ITEM_TEMPLATE, 'enabledToggle'), {
    zIndex: 5,
    props: ['checked'],
    notes: '安卓对应 SwitchCompat；触摸目标 44dp',
  }),
  meta('providers.item.editButton', '编辑提供商', 'button', providerItemPartLayout(ITEM_TEMPLATE, 'editButton'), {
    zIndex: 5,
    props: ['pressed'],
    notes: '打开配置表单（编辑模式）',
  }),
  meta('providers.item.deleteButton', '删除提供商', 'button', providerItemPartLayout(ITEM_TEMPLATE, 'deleteButton'), {
    zIndex: 5,
    props: ['pressed'],
    notes: '触发二次确认',
  }),
  meta('providers.empty', '提供商空状态', 'container', EMPTY, {
    zIndex: 3,
    notes: '本地没有提供商配置时显示',
  }),
  meta('providers.empty.title', '空状态主文案', 'text', EMPTY_TITLE, {
    zIndex: 4,
    notes: '居中',
  }),
  meta('providers.empty.hint', '空状态说明', 'text', EMPTY_HINT, {
    zIndex: 4,
    notes: '居中，次要文本色',
  }),
  meta('providers.deleteBackdrop', '删除确认遮罩', 'modal', DELETE_BACKDROP, {
    zIndex: 30,
    notes: '二次确认弹层遮罩；安卓用 Dialog 的 dim 层',
  }),
  meta('providers.deleteDialog', '删除确认对话框', 'modal', DELETE_DIALOG, {
    zIndex: 31,
    notes: '安卓对应 AlertDialog',
  }),
  meta('providers.deleteDialog.title', '删除确认标题', 'text', DELETE_TITLE, { zIndex: 32 }),
  meta('providers.deleteDialog.text', '删除确认说明', 'text', DELETE_TEXT, { zIndex: 32 }),
  meta('providers.deleteDialog.cancelButton', '取消删除', 'button', DELETE_CANCEL, {
    zIndex: 32,
    props: ['pressed'],
  }),
  meta('providers.deleteDialog.confirmButton', '确认删除', 'button', DELETE_CONFIRM, {
    zIndex: 32,
    props: ['pressed'],
  }),
];

const KIND_COMPONENTS: ComponentMetaInput[] = BUILTIN_PROVIDER_KINDS.map((kind) =>
  meta(
    `providers.form.kind.${kind}`,
    `提供商类型：${kind}`,
    'button',
    PROVIDER_KIND_ITEM_LAYOUTS[kind],
    { zIndex: 22, props: ['selected'], notes: '分段控件，安卓用 MaterialButtonToggleGroup' },
  ),
);

const FORM_COMPONENTS: ComponentMetaInput[] = [
  meta('providers.catalogStatus', '模型厂商加载状态', 'container', { x: 0, y: 80, width: 390, height: 40 }, { zIndex: 5, props: ['status'], notes: '模型提供商列表加载及失败重试；安卓用状态行' }),
  meta('providers.form.protocol', '协议选择', 'input', { x: 16, y: 270, width: 358, height: 44 }, { zIndex: 22, props: ['value', 'disabled'], notes: 'Chat Completions / Responses / Anthropic / Gemini；锁定时disabled' }),
  meta('providers.form.logoLabel', '厂商Logo标签', 'text', { x: 16, y: 168, width: 358, height: 18 }, { zIndex: 22 }),
  meta('providers.form.logoInput', '厂商 Logo 地址', 'input', { x: 16, y: 218, width: 358, height: 44 }, { zIndex: 22, props: ['value', 'disabled'] }),
  meta('page.providers.form', '提供商配置表单容器', 'container', ROOT, {
    zIndex: 20,
    notes: '表单视图根容器；安卓用 Fragment 根 View',
  }),
  meta('providers.form.header', '配置表单页头', 'container', FORM_HEADER, {
    zIndex: 21,
    notes: '返回 + 标题容器',
  }),
  meta('providers.form.backButton', '返回列表', 'button', FORM_BACK, {
    zIndex: 22,
    props: ['pressed'],
    notes: '安卓用 Toolbar 返回键',
  }),
  meta('providers.form.title', '配置表单标题', 'text', FORM_TITLE, { zIndex: 22 }),
  meta('providers.form.kindLabel', '协议', 'text', KIND_LABEL, { zIndex: 22 }),
  ...KIND_COMPONENTS,
  meta('providers.form.kindHint', '官方基址提示', 'text', KIND_HINT, {
    zIndex: 22,
    notes: '仅作 placeholder 提示，切换类型不会覆盖已填值',
  }),
  meta('providers.form.purposeLabel', '模型类型', 'text', PURPOSE_LABEL, { zIndex: 22 }),
  meta('providers.form.purpose', '模型类型选择', 'input', PURPOSE_SELECT, {
    zIndex: 22,
    props: ['value', 'options'],
    notes: '语言推理模型 / 生图模型；生图仅对 OpenAI 兼容与自定义提供商开放',
  }),
  meta('providers.form.purposeError', '模型类型校验提示', 'text', PURPOSE_ERROR, {
    zIndex: 22,
    notes: '提供商类型不支持生图时显示',
  }),
  meta('providers.form.nameLabel', '名称', 'text', NAME_LABEL, { zIndex: 22 }),
  meta('providers.form.nameInput', '名称输入', 'input', NAME_INPUT, {
    zIndex: 22,
    props: ['value'],
    notes: '安卓对应 EditText',
  }),
  meta('providers.form.nameError', '名称校验提示', 'text', NAME_ERROR, {
    zIndex: 22,
    notes: '必填缺失时显示，危险色',
  }),
  meta('providers.form.baseUrlLabel', '基址', 'text', BASE_URL_LABEL, { zIndex: 22 }),
  meta('providers.form.baseUrlInput', '基址输入', 'input', BASE_URL_INPUT, {
    zIndex: 22,
    props: ['value', 'placeholder'],
    notes: 'placeholder 随 kind 变，安卓对应 EditText(inputType=textUri)',
  }),
  meta('providers.form.baseUrlError', '基址校验提示', 'text', BASE_URL_ERROR, {
    zIndex: 22,
    notes: 'URL 非法时显示，危险色',
  }),
  meta('providers.form.apiKeyLabel', 'API Key', 'text', API_KEY_LABEL, { zIndex: 22 }),
  meta('providers.form.apiKeyInput', 'API Key 输入', 'input', API_KEY_INPUT, {
    zIndex: 22,
    props: ['value', 'masked'],
    notes: '默认打码（type=password），安卓对应 EditText(inputType=textPassword)',
  }),
  meta('providers.form.apiKeyRevealButton', '临时显形密钥', 'button', API_KEY_REVEAL, {
    zIndex: 22,
    props: ['pressed', 'revealed'],
    notes: '按住 / 点击临时显示明文',
  }),
  meta('providers.form.defaultModelLabel', '默认模型', 'text', DEFAULT_MODEL_LABEL, { zIndex: 22 }),
  meta('providers.form.defaultModelInput', '默认模型输入', 'input', DEFAULT_MODEL_INPUT, {
    zIndex: 22,
    props: ['value', 'options'],
    notes: '测试成功后映射为可下拉选择（datalist）也可手工输入',
  }),
  meta('providers.form.enabledLabel', '启用状态', 'text', ENABLED_LABEL, { zIndex: 22 }),
  meta('providers.form.enabledToggle', '启用 / 停用', 'button', ENABLED_TOGGLE, {
    zIndex: 22,
    props: ['checked'],
    notes: '安卓对应 SwitchCompat',
  }),
  meta('providers.form.testButton', '测试连接', 'button', TEST_BUTTON, {
    zIndex: 22,
    props: ['disabled', 'loading'],
    notes: '经 createProviderAdapter().listModels() 探测，不落库',
  }),
  meta('providers.form.testResult', '测试结果', 'text', TEST_RESULT, {
    zIndex: 22,
    notes: '成功显示模型数与前几个模型；失败显示规范化错误码与消息',
  }),
  meta('providers.form.saveButton', '保存', 'button', SAVE_BUTTON, {
    zIndex: 22,
    props: ['disabled'],
    notes: '校验通过后写入 providerRepo + 密钥存储',
  }),
  meta('providers.form.cancelButton', '取消', 'button', CANCEL_BUTTON, {
    zIndex: 22,
    props: ['pressed'],
    notes: '放弃编辑返回列表',
  }),
];

/** 本页全部静态部件元数据（列表模板 + 表单 + 删除弹层）。 */
export const PROVIDERS_COMPONENTS: ComponentMetaInput[] = [...LIST_COMPONENTS, ...FORM_COMPONENTS];

/** 供测试断言默认坐标的完整 ComponentMeta 列表。 */
export function providersComponentMetaList(): ComponentMeta[] {
  return PROVIDERS_COMPONENTS.map((input) => ({
    id: input.id,
    page: input.page,
    role: input.role ?? 'container',
    label: input.label,
    anchor: { frameWidth: 390, frameHeight: 844, ...input.layout },
    zIndex: input.zIndex ?? 0,
    ...(input.props === undefined ? {} : { props: [...input.props] }),
    ...(input.notes === undefined ? {} : { notes: input.notes }),
  }));
}
const META_BY_ID = new Map(PROVIDERS_COMPONENTS.map((entry) => [entry.id, entry]));

/** 按 id 读取本页静态部件元数据（页面渲染与清单共用同一份定义）。 */
export function providerMetaById(id: string): ComponentMetaInput {
  const found = META_BY_ID.get(id);
  if (found === undefined) throw new Error(`[providers] 未知部件 id：${id}`);
  return found;
}
